import { Modal, ModalContent } from '@leland-dev/leland-ui-library';
import React, {
  type FC,
  type ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';

import { getPagination } from '../../../utils/pagination';
import InfiniteScroll from '../../common/InfiniteScroll';
import LoadingIndicator from '../../common/LoadingIndicator';
import Review from '../../review/Review';

import { type BaseAllReviewsModalReviewFragment } from './__generated-gql-types__/BaseAllReviewsModal.generated';

interface BaseAllReviewsModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  title: string;
  loading: boolean;
  total: number;
  fetchMore: (nextPage: number) => void;
  header?: ReactNode;
  reviews: Possible<BaseAllReviewsModalReviewFragment[]>;
}

export const BASE_ALL_REVIEWS_MODAL_PAGE_META = {
  defaultPage: 1,
  pageSize: 10,
};

export const BaseAllReviewsModal: FC<BaseAllReviewsModalProps> = ({
  open,
  setOpen,
  title,
  loading,
  total,
  fetchMore,
  header,
  reviews,
}) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(
    BASE_ALL_REVIEWS_MODAL_PAGE_META.defaultPage,
  );

  const pagination = useMemo(() => {
    return getPagination(
      total,
      page,
      BASE_ALL_REVIEWS_MODAL_PAGE_META.pageSize,
    );
  }, [total, page]);

  const onFetchMore = useCallback(() => {
    const nextPage = page + 1;
    fetchMore(nextPage);
    setPage(nextPage);
  }, [fetchMore, page]);

  return (
    <Modal open={open} onOpenChange={setOpen}>
      <ModalContent header={title}>
        {loading ? (
          <LoadingIndicator
            className="p-8"
            iconClassName="size-20"
            text="Loading reviews..."
          />
        ) : (
          <InfiniteScroll
            className="w-full pb-8"
            loading={loading}
            root={rootRef.current}
            hasMore={page < pagination.totalPages}
            fetchMore={onFetchMore}
          >
            <div className="mx-6 space-y-6 divide-y">
              {header ? <div className="w-full pt-6">{header}</div> : null}
              <div className="flex w-full flex-col items-start justify-start space-y-6 divide-y border-leland-gray-stroke">
                {reviews?.map((review) => (
                  <Review
                    key={review.id}
                    className="w-full border-leland-gray-stroke pt-6"
                    review={review}
                    viewAsCoach
                    hideFor
                  />
                ))}
              </div>
            </div>
          </InfiniteScroll>
        )}
      </ModalContent>
    </Modal>
  );
};
